import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getColors, typography, spacing, borderRadius, getShadows } from '../styles/theme';
import { getSubscriptionStatus, cancelClubSubscription } from '../services/subscriptionService';

const MembershipScreen = ({ navigation }) => {
  const { isDarkMode } = useTheme();
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, [appUser?.id]);

  const loadSubscriptionData = async () => {
    if (!appUser?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getSubscriptionStatus(appUser.id, appUser.email);
      setSubscriptionData(data);
    } catch (error) {
      console.error('Error loading subscription data from Stripe:', error);
      // Fallback to database data if Stripe call fails
      if (appUser.tier === 'PRO') {
        // Use database subscription info as fallback
        setSubscriptionData({
          hasSubscription: true,
          activeSubscription: {
            id: appUser.stripeSubscriptionId || 'unknown',
            status: appUser.subscriptionStatus || 'active',
            created: appUser.subscriptionStartedAt ? new Date(appUser.subscriptionStartedAt).getTime() / 1000 : Date.now() / 1000,
            currentPeriodEnd: appUser.subscriptionEndsAt ? new Date(appUser.subscriptionEndsAt).getTime() / 1000 : (Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000,
            cancelAtPeriodEnd: false,
          },
          customer: {
            id: appUser.stripeCustomerId,
            email: appUser.email,
          },
        });
      } else {
        showToast('Failed to load subscription details', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleCancelSubscription = () => {
    const activeSub = subscriptionData?.activeSubscription;
    if (!activeSub) return;

    const cancelAtPeriodEnd = activeSub.cancelAtPeriodEnd;
    const message = cancelAtPeriodEnd
      ? 'Your subscription is already set to cancel at the end of the billing period. Would you like to reactivate it instead?'
      : 'Are you sure you want to cancel your subscription? You can cancel immediately or at the end of your billing period.';

    const options = cancelAtPeriodEnd
      ? [
          { text: 'Keep Canceled', style: 'cancel' },
          {
            text: 'Reactivate',
            onPress: () => confirmReactivate(activeSub.id),
          },
        ]
      : [
          { text: 'Keep Subscription', style: 'cancel' },
          {
            text: 'Cancel at Period End',
            onPress: () => confirmCancel(activeSub.id, false),
          },
          {
            text: 'Cancel Immediately',
            style: 'destructive',
            onPress: () => confirmCancel(activeSub.id, true),
          },
        ];

    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        if (cancelAtPeriodEnd) {
          confirmReactivate(activeSub.id);
        } else {
          confirmCancel(activeSub.id, false);
        }
      }
    } else {
      Alert.alert('Cancel Subscription', message, options);
    }
  };

  const confirmReactivate = async (subscriptionId) => {
    try {
      setCanceling(true);
      await cancelClubSubscription(subscriptionId, false, true);
      showToast('Subscription reactivated successfully', 'success');
      // Reload subscription data
      await loadSubscriptionData();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      showToast('Failed to reactivate subscription', 'error');
    } finally {
      setCanceling(false);
    }
  };

  const confirmCancel = async (subscriptionId, immediately) => {
    try {
      setCanceling(true);
      await cancelClubSubscription(subscriptionId, immediately);
      showToast(
        immediately
          ? 'Subscription canceled successfully'
          : 'Subscription will cancel at the end of the billing period',
        'success'
      );
      // Reload subscription data
      await loadSubscriptionData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showToast('Failed to cancel subscription', 'error');
    } finally {
      setCanceling(false);
    }
  };

  const styles = createStyles(colors, shadows, isDarkMode);

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
          Loading membership details...
        </Text>
      </View>
    );
  }

  const activeSub = subscriptionData?.activeSubscription;
  const hasActiveSubscription = !!activeSub && (activeSub.status === 'active' || activeSub.status === 'trialing');

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.navigate('Map')} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.text} />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Your Membership</Text>
      </View>

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {hasActiveSubscription ? (
          <>
            {/* Active Subscription Card */}
            <View style={[styles.card, { backgroundColor: colors.surface }]}>
              <View style={styles.cardHeader}>
                <View style={[styles.statusBadge, { backgroundColor: colors.primary + '20' }]}>
                  <Text style={[styles.statusText, { color: colors.primary }]}>
                    {activeSub.status === 'trialing' ? 'Trial' : 'Active'}
                  </Text>
                </View>
                <Text style={[styles.planName, { color: colors.text }]}>Club Pro</Text>
              </View>

              <View style={styles.detailsSection}>
                <View style={styles.detailRow}>
                  <Icon name="calendar-today" size={20} color={colors.textSecondary} />
                  <View style={styles.detailContent}>
                    <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>
                      Started
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {formatDate(activeSub.created)}
                    </Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <Icon name="event" size={20} color={colors.textSecondary} />
                  <View style={styles.detailContent}>
                    <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>
                      {activeSub.cancelAtPeriodEnd ? 'Ends on' : 'Next billing date'}
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {formatDate(activeSub.currentPeriodEnd)}
                    </Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <Icon name="attach-money" size={20} color={colors.textSecondary} />
                  <View style={styles.detailContent}>
                    <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>
                      Amount
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>$30.00/month</Text>
                  </View>
                </View>
              </View>

              {activeSub.cancelAtPeriodEnd && (
                <View style={[styles.warningBanner, { backgroundColor: colors.warning + '20' }]}>
                  <Icon name="info" size={20} color={colors.warning} />
                  <Text style={[styles.warningText, { color: colors.warning }]}>
                    Your subscription will cancel on {formatDate(activeSub.currentPeriodEnd)}. You'll continue to have access until then.
                  </Text>
                </View>
              )}
            </View>

            {/* Cancel Button */}
            <TouchableOpacity
              style={[
                styles.cancelButton,
                {
                  backgroundColor: activeSub.cancelAtPeriodEnd ? colors.primary : colors.error + '20',
                  borderColor: activeSub.cancelAtPeriodEnd ? colors.primary : colors.error,
                },
              ]}
              onPress={handleCancelSubscription}
              disabled={canceling}
            >
              {canceling ? (
                <ActivityIndicator size="small" color={colors.text} />
              ) : (
                <>
                  <Icon
                    name={activeSub.cancelAtPeriodEnd ? 'refresh' : 'cancel'}
                    size={20}
                    color={activeSub.cancelAtPeriodEnd ? '#FFF' : colors.error}
                  />
                  <Text
                    style={[
                      styles.cancelButtonText,
                      {
                        color: activeSub.cancelAtPeriodEnd ? '#FFF' : colors.error,
                      },
                    ]}
                  >
                    {activeSub.cancelAtPeriodEnd ? 'Reactivate Subscription' : 'Cancel Subscription'}
                  </Text>
                </>
              )}
            </TouchableOpacity>

            {/* Benefits Section */}
            <View style={[styles.card, { backgroundColor: colors.surface }]}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>What's Included</Text>
              <View style={styles.benefitsList}>
                {[
                  'Everything in Free',
                  'Paid events & payments',
                  'Tournament management tools',
                  'Attendance tracking',
                  'Priority support',
                ].map((benefit, index) => (
                  <View key={index} style={styles.benefitItem}>
                    <Icon name="check-circle" size={20} color={colors.primary} />
                    <Text style={[styles.benefitText, { color: colors.text }]}>{benefit}</Text>
                  </View>
                ))}
              </View>
            </View>
          </>
        ) : (
          <View style={[styles.card, { backgroundColor: colors.surface }]}>
            <Icon name="info" size={48} color={colors.textSecondary} />
            <Text style={[styles.noSubscriptionTitle, { color: colors.text }]}>
              No Active Subscription
            </Text>
            <Text style={[styles.noSubscriptionText, { color: colors.textSecondary }]}>
              You don't have an active Club Pro subscription. Upgrade to unlock paid events, tournament tools, and more.
            </Text>
            <TouchableOpacity
              style={[styles.upgradeButton, { backgroundColor: colors.primary }]}
              onPress={() => {
                // Navigate to pricing but show the pricing screen content
                navigation.navigate('Pricing');
              }}
            >
              <Text style={styles.upgradeButtonText}>View Plans</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const createStyles = (colors, shadows, isDarkMode) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      paddingTop: Platform.OS === 'ios' ? 50 : 20,
    },
    centered: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
    },
    loadingText: {
      marginTop: spacing.md,
      fontSize: typography.body,
    },
    header: {
      padding: spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    backButton: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    backText: {
      fontSize: typography.body,
      color: colors.text,
      marginLeft: spacing.xs,
    },
    headerTitle: {
      fontSize: typography.h2,
      fontWeight: typography.bold,
      color: colors.text,
    },
    content: {
      flex: 1,
    },
    scrollContent: {
      padding: spacing.lg,
      paddingBottom: spacing.xxl,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      marginBottom: spacing.lg,
      ...shadows.medium,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.xl,
    },
    statusBadge: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      borderRadius: borderRadius.pill,
    },
    statusText: {
      fontSize: typography.caption,
      fontWeight: typography.semiBold,
      textTransform: 'uppercase',
    },
    planName: {
      fontSize: typography.h3,
      fontWeight: typography.bold,
    },
    detailsSection: {
      gap: spacing.lg,
    },
    detailRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.md,
    },
    detailContent: {
      flex: 1,
    },
    detailLabel: {
      fontSize: typography.caption,
      marginBottom: spacing.xs,
    },
    detailValue: {
      fontSize: typography.body,
      fontWeight: typography.semiBold,
    },
    warningBanner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      padding: spacing.md,
      borderRadius: borderRadius.md,
      marginTop: spacing.lg,
    },
    warningText: {
      flex: 1,
      fontSize: typography.bodySmall,
      lineHeight: 20,
    },
    cancelButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      padding: spacing.md,
      borderRadius: borderRadius.lg,
      borderWidth: 2,
      marginBottom: spacing.lg,
    },
    cancelButtonText: {
      fontSize: typography.h6,
      fontWeight: typography.semiBold,
    },
    sectionTitle: {
      fontSize: typography.h5,
      fontWeight: typography.bold,
      marginBottom: spacing.lg,
    },
    benefitsList: {
      gap: spacing.md,
    },
    benefitItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    benefitText: {
      fontSize: typography.body,
      flex: 1,
    },
    noSubscriptionTitle: {
      fontSize: typography.h4,
      fontWeight: typography.bold,
      textAlign: 'center',
      marginTop: spacing.lg,
      marginBottom: spacing.md,
    },
    noSubscriptionText: {
      fontSize: typography.body,
      textAlign: 'center',
      lineHeight: 24,
      marginBottom: spacing.xl,
    },
    upgradeButton: {
      padding: spacing.md,
      borderRadius: borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    upgradeButtonText: {
      color: '#FFF',
      fontSize: typography.h6,
      fontWeight: typography.semiBold,
    },
  });

export default MembershipScreen;
